import { isTimePermissionEnabled } from '../config/applicationFeatures';
import type {
  AttendanceReportFilters,
  AttendanceReportRow,
} from '../types/attendanceReport';
import type { ReportPreviewRow } from '../types/report';
import { formatDisplayLabel } from './camelCaseLabel';
import { getAttendanceStatusLabel } from './attendanceStatus';
import { dayStatusFromReportValues } from './attendanceDayStatus';
import { formatMinutes, formatTime } from './dateTime';
import {
  attendancePunchChannelLabel,
  isMixedPunchActionSource,
  punchActionSourceLabel,
} from './attendanceEventSource';
import { resolveAttendanceLocationMode } from './attendanceLocationMode';
import {
  resolveCheckedOutWorkHoursCoverage,
  resolveIncompleteWorkKind,
} from './workHoursCoverage';
import {
  actualTotalWorkMinutes,
  formatTotalWorkHours,
  policyMinutes,
  resolveAttendanceSpreadMinutes,
  resolveTotalHoursCoverage,
} from './totalHours';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const EMPTY = '—';

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function createDefaultAttendanceReportFilters(
  now = new Date(),
): AttendanceReportFilters {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return {
    attendanceDate: `${year}-${pad2(month)}-${pad2(day)}`,
    department: '',
    status: '',
  };
}

export function formatAttendanceReportDateLabel(
  attendanceDate: string,
): string {
  const parts = parseDateParts(attendanceDate);
  if (!parts) return attendanceDate;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${pad2(parts.day)} ${months[parts.month - 1]} ${parts.year}`;
}

function parseDateParts(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = value.trim().match(DATE_ONLY);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  return { year, month, day };
}

export function validateAttendanceReportDate(
  attendanceDate: string,
): string | null {
  if (!parseDateParts(attendanceDate)) {
    return 'Use a YYYY-MM-DD date to load employee attendance.';
  }
  return null;
}

/** Single-day ATTENDANCE_REGISTER filters (fromDate = toDate). */
export function attendanceRegisterFilters(
  attendanceDate: string,
  department: string,
): { fromDate: string; toDate: string; departmentId?: string } | null {
  if (validateAttendanceReportDate(attendanceDate)) return null;
  const date = attendanceDate.trim();
  const filters: { fromDate: string; toDate: string; departmentId?: string } = {
    fromDate: date,
    toDate: date,
  };
  const dept = department.trim();
  if (/^\d+$/.test(dept) && Number(dept) > 0) {
    filters.departmentId = dept;
  }
  return filters;
}

function firstValue(values: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (
      values[key] !== undefined &&
      values[key] !== null &&
      values[key] !== ''
    ) {
      return values[key];
    }
  }
  return null;
}

function findSourceKey(
  values: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const key of keys) {
    if (
      values[key] !== undefined &&
      values[key] !== null &&
      values[key] !== ''
    ) {
      return key;
    }
  }
  return null;
}

function asDisplayText(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || EMPTY;
  }
  return EMPTY;
}

function asTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return EMPTY;
    const formatted = formatTime(trimmed);
    return formatted === 'Not available' ? trimmed : formatted;
  }
  return asDisplayText(value);
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asTruthyFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function asWorkedHours(value: unknown, sourceKey: string | null): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (sourceKey && /minute/i.test(sourceKey)) {
      const formatted = formatMinutes(value);
      return formatted === 'Not available' ? EMPTY : formatted;
    }
    if (!Number.isInteger(value) || value > 48) {
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);
      return formatMinutes(hours * 60 + Math.max(0, minutes));
    }
    return formatMinutes(value * 60);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return EMPTY;
    if (/h|m/i.test(trimmed)) return trimmed;
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum)) {
      return asWorkedHours(asNum, sourceKey);
    }
    return trimmed;
  }
  return EMPTY;
}

const EMPLOYEE_KEYS = [
  'employeeName',
  'employeeFullName',
  'displayName',
  'fullName',
  'name',
  'employee',
];
const EMPLOYEE_NUMBER_KEYS = ['employeeNumber', 'employeeCode'];
const CHECK_IN_KEYS = [
  'firstCheckInAt',
  'firstCheckInLocal',
  'checkInTime',
  'checkIn',
  'checkInLocalTime',
  'inTime',
  'punchInTime',
];
const CHECK_OUT_KEYS = [
  'lastCheckOutAt',
  'lastCheckOutLocal',
  'checkOutTime',
  'checkOut',
  'checkOutLocalTime',
  'outTime',
  'punchOutTime',
];
const WORKED_KEYS = [
  'netWorkMinutes',
  'workedMinutes',
  'grossWorkMinutes',
  'totalWorkedMinutes',
  'workedHours',
  'totalWorkedHours',
  'workedDuration',
  'totalWorkedDuration',
];
const SPREAD_KEYS = [
  'spreadMinutes',
  'spread_minutes',
  'spreadDuration',
  'spread_duration',
];
const POLICY_LUNCH_KEYS = [
  'policyLunchBreakMinutes',
  'policy_lunch_break_minutes',
  'configuredLunchBreakMinutes',
  'configured_lunch_break_minutes',
  'lunchBreakMinutesConfigured',
];
const BREAK_KEYS = [
  'breakMinutes',
  'lunchBreakMinutes',
  'totalBreakMinutes',
];
const STATUS_CODE_KEYS = [
  'statusCode',
  'attendanceStatusCode',
  'status',
  'attendanceStatus',
];
const STATUS_LABEL_KEYS = ['statusLabel', 'statusName', 'attendanceStatusName'];
const DEPARTMENT_KEYS = ['departmentName', 'department', 'departmentId'];
const DATE_KEYS = ['attendanceDate', 'date'];
const LATE_MINUTE_KEYS = ['lateMinutes'];
const LATE_FLAG_KEYS = ['isLate', 'late'];
const SHORT_HOURS_MINUTE_KEYS = ['shortHoursMinutes', 'shortHourMinutes'];
const SHORT_HOURS_FLAG_KEYS = ['isShortHours', 'shortHoursFlag'];
const EXPECTED_WORK_MINUTE_KEYS = [
  'expectedWorkMinutes',
  'expectedWorkMins',
  'expected_work_minutes',
];
const MISSING_CHECKOUT_FLAG_KEYS = [
  'missingCheckOut',
  'missingCheckout',
  'isMissingCheckout',
];
const ATTENDANCE_MODE_KEYS = [
  'attendanceMode',
  'attendance_mode',
  'attendanceSource',
];
const CHECK_IN_LOCATION_KEYS = [
  'checkInLocationName',
  'firstCheckInLocationName',
  'checkInLocation',
  'check_in_location_name',
];
const CHECK_OUT_LOCATION_KEYS = [
  'checkOutLocationName',
  'lastCheckOutLocationName',
  'checkOutLocation',
  'check_out_location_name',
];
const CHECK_IN_SOURCE_KEYS = [
  'checkInSource',
  'firstCheckInSource',
  'checkInAttendanceSource',
  'check_in_source',
];
const CHECK_OUT_SOURCE_KEYS = [
  'checkOutSource',
  'lastCheckOutSource',
  'checkOutAttendanceSource',
  'check_out_source',
];
const CHECK_IN_ORIGINAL_SOURCE_KEYS = [
  'checkInOriginalSource',
  'firstCheckInOriginalSource',
  'check_in_original_source',
];
const CHECK_OUT_ORIGINAL_SOURCE_KEYS = [
  'checkOutOriginalSource',
  'lastCheckOutOriginalSource',
  'check_out_original_source',
];
const CHECK_IN_KIOSK_NAME_KEYS = [
  'checkInKioskName',
  'firstCheckInKioskName',
  'check_in_kiosk_name',
];
const CHECK_OUT_KIOSK_NAME_KEYS = [
  'checkOutKioskName',
  'lastCheckOutKioskName',
  'check_out_kiosk_name',
];
const CHECK_IN_KIOSK_ID_KEYS = [
  'checkInKioskId',
  'firstCheckInKioskId',
  'check_in_kiosk_id',
];
const CHECK_OUT_KIOSK_ID_KEYS = [
  'checkOutKioskId',
  'lastCheckOutKioskId',
  'check_out_kiosk_id',
];
const CHECK_IN_FACE_KEYS = [
  'checkInFaceVerified',
  'check_in_face_verified',
];
const CHECK_OUT_FACE_KEYS = [
  'checkOutFaceVerified',
  'check_out_face_verified',
];
const CHECK_IN_DEVICE_KEYS = [
  'checkInDeviceIdentifier',
  'check_in_device_identifier',
];
const CHECK_OUT_DEVICE_KEYS = [
  'checkOutDeviceIdentifier',
  'check_out_device_identifier',
];
const LEAVE_TYPE_KEYS = [
  'leaveTypeName',
  'leave_type_name',
  'leaveName',
];
const LEAVE_SESSION_NAME_KEYS = [
  'leaveSessionName',
  'leave_session_name',
  'sessionName',
];
const LEAVE_SESSION_TYPE_KEYS = [
  'leaveSessionType',
  'leave_session_type',
  'sessionType',
];
const LEAVE_DAYS_KEYS = ['leaveDays', 'leave_days'];
const HALF_DAY_LEAVE_FLAG_KEYS = ['isHalfDayLeave', 'is_half_day_leave'];
const PERMISSION_MINUTE_KEYS = ['permissionMinutes', 'permission_minutes'];
const PERMISSION_TYPE_KEYS = [
  'permissionTypeName',
  'permission_type_name',
  'permissionType',
];
const PERMISSION_START_KEYS = [
  'permissionStartTime',
  'permission_start_time',
];
const PERMISSION_END_KEYS = ['permissionEndTime', 'permission_end_time'];
const PERMISSION_USED_KEYS = [
  'permissionUsed',
  'permission_used',
  'latePermissionUsed',
];

export const HALF_DAY_LEAVE_STATUS_LABEL = 'Half-Day Leave';
export const HALF_DAY_PRESENT_AND_LEAVE_STATUS_LABEL =
  'Half-Day Present + Half-Day Leave';

type StatusTone = AttendanceReportRow['statusTone'];

function explicitFlag(value: unknown): boolean | null {
  if (value === null || value === undefined || value === '') return null;
  return asTruthyFlag(value);
}

function isOffDay(
  values: Record<string, unknown>,
  code: string | null,
): boolean {
  return (
    code === 'WEEKLY_OFF' ||
    code === 'HOLIDAY' ||
    asTruthyFlag(values.isWeeklyOff) ||
    asTruthyFlag(values.isHoliday)
  );
}

function isOnLeaveStatus(
  values: Record<string, unknown>,
  code: string | null,
): boolean {
  const leaveType = asDisplayText(firstValue(values, LEAVE_TYPE_KEYS));
  const leaveDays = asNumber(firstValue(values, LEAVE_DAYS_KEYS));
  return (
    code === 'ON_LEAVE' ||
    code === 'HALF_DAY_LEAVE' ||
    asTruthyFlag(values.isOnLeave) ||
    leaveType !== EMPTY ||
    (typeof leaveDays === 'number' && leaveDays > 0) ||
    /on.?leave/i.test(asDisplayText(firstValue(values, STATUS_LABEL_KEYS)))
  );
}

function humanizeCode(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function statusCodeFromValues(values: Record<string, unknown>): string | null {
  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  return rawCode !== EMPTY
    ? rawCode.trim().toUpperCase().replace(/\s+/g, '_')
    : null;
}

export function isHalfDayLeaveDay(values: Record<string, unknown>): boolean {
  const code = statusCodeFromValues(values);
  if (code === 'HALF_DAY_LEAVE') return true;
  if (asTruthyFlag(firstValue(values, HALF_DAY_LEAVE_FLAG_KEYS))) return true;
  const leaveDays = asNumber(firstValue(values, LEAVE_DAYS_KEYS));
  if (typeof leaveDays === 'number' && leaveDays > 0 && leaveDays < 1) {
    return true;
  }
  const session = asDisplayText(firstValue(values, LEAVE_SESSION_TYPE_KEYS));
  const sessionName = asDisplayText(firstValue(values, LEAVE_SESSION_NAME_KEYS));
  const sessionText = `${session} ${sessionName}`.toLowerCase();
  return /first.?half|second.?half|half.?day/.test(sessionText);
}

function formatLeaveDetails(values: Record<string, unknown>): string | null {
  const leaveType = formatDisplayLabel(
    asDisplayText(firstValue(values, LEAVE_TYPE_KEYS)),
    EMPTY,
  );
  const sessionName = asDisplayText(firstValue(values, LEAVE_SESSION_NAME_KEYS));
  const sessionType = asDisplayText(firstValue(values, LEAVE_SESSION_TYPE_KEYS));
  const sessionLabel =
    sessionName !== EMPTY
      ? sessionName
      : sessionType !== EMPTY
        ? humanizeCode(sessionType)
        : null;
  const isHalf = isHalfDayLeaveDay(values);

  if (leaveType !== EMPTY && sessionLabel) {
    const skipSession = /^full(\s|-)?day$/i.test(sessionLabel) && !isHalf;
    return skipSession ? leaveType : `${leaveType} (${sessionLabel})`;
  }
  if (leaveType !== EMPTY) {
    return isHalf ? `${leaveType} (Half Day)` : leaveType;
  }
  if (sessionLabel) {
    return isHalf && !/half/i.test(sessionLabel)
      ? `${sessionLabel} leave`
      : sessionLabel;
  }
  if (isHalf) return 'Half-day leave';
  return null;
}

function formatPermissionDetails(
  values: Record<string, unknown>,
): string | null {
  const minutes = asNumber(firstValue(values, PERMISSION_MINUTE_KEYS));
  const typeRaw = asDisplayText(firstValue(values, PERMISSION_TYPE_KEYS));
  const start = asTime(firstValue(values, PERMISSION_START_KEYS));
  const end = asTime(firstValue(values, PERMISSION_END_KEYS));
  const used = asTruthyFlag(firstValue(values, PERMISSION_USED_KEYS));
  const hasMinutes = typeof minutes === 'number' && minutes > 0;
  if (!hasMinutes && typeRaw === EMPTY && !used) return null;

  const typeLabel =
    typeRaw !== EMPTY
      ? /permission/i.test(typeRaw)
        ? typeRaw
        : `${humanizeCode(typeRaw)} permission`
      : 'Permission';
  const duration =
    hasMinutes && asWorkedHours(minutes, 'permissionMinutes') !== EMPTY
      ? asWorkedHours(minutes, 'permissionMinutes')
      : null;
  const window =
    start !== EMPTY && end !== EMPTY
      ? `${start} – ${end}`
      : start !== EMPTY
        ? start
        : null;

  const parts = [typeLabel];
  if (duration) parts.push(duration);
  if (window) parts.push(`(${window})`);
  return parts.join(' ');
}

/**
 * Leave type/session and approved permission text for the Daily Attendance row.
 */
export function formatAttendanceReportDayDetails(
  values: Record<string, unknown>,
): string {
  const parts = [
    formatLeaveDetails(values),
    isTimePermissionEnabled() ? formatPermissionDetails(values) : '',
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join('\n') : EMPTY;
}

function isWfhStatus(
  values: Record<string, unknown>,
  code: string | null,
  hasCheckIn = false,
): boolean {
  // Approved WFH without a check-in is not attendance.
  if (!hasCheckIn) return false;
  const attendanceMode = asDisplayText(
    firstValue(values, ATTENDANCE_MODE_KEYS),
  )
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  return (
    code === 'WFH' ||
    asTruthyFlag(values.isWfh) ||
    attendanceMode === 'WFH' ||
    attendanceMode === 'WFH_APP' ||
    /\bwfh\b|work.?from.?home/i.test(
      asDisplayText(firstValue(values, STATUS_LABEL_KEYS)),
    )
  );
}

export function detectShortHours(values: Record<string, unknown>): boolean {
  // Short Hours is final only after a valid check-out.
  if (asTime(firstValue(values, CHECK_OUT_KEYS)) === EMPTY) return false;
  const workedKey = findSourceKey(values, WORKED_KEYS);
  const workedMinutes = asNumber(workedKey ? values[workedKey] : null);
  const expectedWorkMinutes = asNumber(
    firstValue(values, EXPECTED_WORK_MINUTE_KEYS),
  );
  const incompleteKind = resolveIncompleteWorkKind(
    workedMinutes,
    expectedWorkMinutes,
  );
  if (incompleteKind === 'covered' || incompleteKind === 'half_day_absent') {
    return false;
  }
  if (incompleteKind === 'short_hours') return true;

  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  const code =
    rawCode !== EMPTY
      ? rawCode.trim().toUpperCase().replace(/\s+/g, '_')
      : null;
  if (code === 'SHORT_HOURS' || code === 'SHORT_HOUR') return true;
  if (asTruthyFlag(firstValue(values, SHORT_HOURS_FLAG_KEYS))) return true;
  const minutes = asNumber(firstValue(values, SHORT_HOURS_MINUTE_KEYS));
  return typeof minutes === 'number' && minutes > 0;
}

export function detectHalfDayAbsentWork(
  values: Record<string, unknown>,
  hasCheckedOut: boolean,
): boolean {
  if (!hasCheckedOut || isHalfDayLeaveDay(values)) return false;
  const workedKey = findSourceKey(values, WORKED_KEYS);
  const workedMinutes = asNumber(workedKey ? values[workedKey] : null);
  const expectedWorkMinutes = asNumber(
    firstValue(values, EXPECTED_WORK_MINUTE_KEYS),
  );
  const incompleteKind = resolveIncompleteWorkKind(
    workedMinutes,
    expectedWorkMinutes,
  );
  if (incompleteKind === 'half_day_absent') return true;
  if (incompleteKind === 'short_hours' || incompleteKind === 'covered') {
    return false;
  }
  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  const code =
    rawCode !== EMPTY
      ? rawCode.trim().toUpperCase().replace(/\s+/g, '_')
      : null;
  return code === 'HALF_DAY' || asTruthyFlag(values.isHalfDay);
}

export function detectMissingCheckout(
  values: Record<string, unknown>,
  hasCheckedOut: boolean,
): boolean {
  if (hasCheckedOut) return false;
  if (asTruthyFlag(firstValue(values, MISSING_CHECKOUT_FLAG_KEYS))) return true;
  if (explicitFlag(values.isPresent) === true) return true;
  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  const code =
    rawCode !== EMPTY
      ? rawCode.trim().toUpperCase().replace(/\s+/g, '_')
      : null;
  const hasCheckIn = asTime(firstValue(values, CHECK_IN_KEYS)) !== EMPTY;
  return (
    code === 'PRESENT' ||
    code === 'CHECKED_IN' ||
    (hasCheckIn && (code === 'WFH' || asTruthyFlag(values.isWfh)))
  );
}

/**
 * Resolves display status for the admin table.
 * The backend day status (Expected / Half-Day Work Hours, approved leave and
 * late-arrival policy) wins; the code below only covers rows without it.
 * Early exit is intentionally not a status — hours still show when checked out.
 */
export function resolveAttendanceReportStatus(
  values: Record<string, unknown>,
): {
  label: string;
  tone: StatusTone;
} {
  const backendStatus = dayStatusFromReportValues(values);
  if (backendStatus) {
    return { label: backendStatus.label, tone: backendStatus.tone };
  }
  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  const code =
    rawCode !== EMPTY
      ? rawCode.trim().toUpperCase().replace(/\s+/g, '_')
      : null;
  const lateMinutes = asNumber(firstValue(values, LATE_MINUTE_KEYS));
  const isLateFlag = asTruthyFlag(firstValue(values, LATE_FLAG_KEYS));
  const isLate =
    isLateFlag || (typeof lateMinutes === 'number' && lateMinutes > 0);
  const hasCheckIn = asTime(firstValue(values, CHECK_IN_KEYS)) !== EMPTY;
  const hasCheckedOut = asTime(firstValue(values, CHECK_OUT_KEYS)) !== EMPTY;
  const presentFlag = explicitFlag(values.isPresent);
  const shortHours = detectShortHours(values);
  const halfDayLeave = isHalfDayLeaveDay(values);
  const halfDayAbsent = detectHalfDayAbsentWork(values, hasCheckedOut);

  if (halfDayLeave) {
    return {
      label: hasCheckIn
        ? HALF_DAY_PRESENT_AND_LEAVE_STATUS_LABEL
        : HALF_DAY_LEAVE_STATUS_LABEL,
      tone: 'leave',
    };
  }

  if (isOnLeaveStatus(values, code)) {
    return { label: formatLeaveDetails(values) || 'On Leave', tone: 'leave' };
  }

  if (isWfhStatus(values, code, hasCheckIn)) {
    return { label: 'WFH', tone: 'wfh' };
  }

  const approvedCompensatoryWork = asTruthyFlag(values.isCompensatoryWork);
  // Approved compensatory work makes holiday / weekly off a required work day.
  if (!approvedCompensatoryWork && isOffDay(values, code)) {
    if (code === 'HOLIDAY' || asTruthyFlag(values.isHoliday)) {
      return { label: 'Holiday', tone: 'neutral' };
    }
    return { label: 'Weekly Off', tone: 'neutral' };
  }

  if (
    code === 'LOP' ||
    code === 'ABSENT' ||
    asTruthyFlag(values.isAbsent) ||
    (presentFlag === false && !hasCheckIn) ||
    // Approved WFH / Comp Off without any punch is Absent, not Present.
    (!hasCheckIn &&
      (asTruthyFlag(values.isWfh) ||
        code === 'WFH' ||
        approvedCompensatoryWork))
  ) {
    return { label: 'LOP', tone: 'absent' };
  }

  // Hours after checkout: half-day or less is Half-Day Absent; above half
  // but under expected is Short Hours.
  if (halfDayAbsent || code === 'HALF_DAY') {
    return { label: 'Half-Day Absent', tone: 'absent' };
  }

  if (shortHours) {
    return { label: 'Short Hours', tone: 'shortHours' };
  }

  if (isLate && (code === 'PRESENT' || presentFlag === true || !code)) {
    return { label: 'Late', tone: 'late' };
  }

  if (code === 'PRESENT' || presentFlag === true) {
    return { label: 'Present', tone: 'present' };
  }

  if (code === 'CHECKED_IN') {
    return isLate
      ? { label: 'Late', tone: 'late' }
      : { label: 'Present', tone: 'present' };
  }

  if (code === 'CHECKED_OUT') {
    return isLate
      ? { label: 'Late', tone: 'late' }
      : { label: 'Present', tone: 'present' };
  }

  if (code && code !== EMPTY) {
    const label = getAttendanceStatusLabel(code);
    if (label !== 'Status unavailable') {
      const lower = label.toLowerCase();
      if (lower.includes('leave')) return { label: 'On Leave', tone: 'leave' };
      if (lower.includes('wfh') || lower.includes('home')) {
        return { label: 'WFH', tone: 'wfh' };
      }
      if (lower.includes('absent')) return { label: 'Absent', tone: 'absent' };
      if (lower.includes('short'))
        return { label: 'Short Hours', tone: 'shortHours' };
      if (lower.includes('late')) return { label: 'Late', tone: 'late' };
      if (lower.includes('present') || lower.includes('checked')) {
        return { label: 'Present', tone: 'present' };
      }
      return { label, tone: 'neutral' };
    }
  }

  const labelText = asDisplayText(firstValue(values, STATUS_LABEL_KEYS));
  if (labelText !== EMPTY) {
    return { label: labelText, tone: 'neutral' };
  }

  return { label: EMPTY, tone: 'neutral' };
}

function optionalDisplay(value: unknown): string | null {
  const text = asDisplayText(value);
  return text === EMPTY ? null : text;
}

function formatCheckInCheckOutType(
  checkInLabel: string | null,
  checkOutLabel: string | null,
): string | null {
  if (!checkInLabel && !checkOutLabel) return null;
  return `Check-In: ${checkInLabel ?? EMPTY} / Check-Out: ${checkOutLabel ?? EMPTY}`;
}

function formatSharedOrPairedLabel(
  checkInValue: string | null,
  checkOutValue: string | null,
): string | null {
  if (!checkInValue && !checkOutValue) return null;
  if (checkInValue && checkOutValue && checkInValue !== checkOutValue) {
    return `Check-In: ${checkInValue} / Check-Out: ${checkOutValue}`;
  }
  return checkInValue ?? checkOutValue;
}

/** Type column text: both punch channels, plus office and kiosk when present. */
export function formatAttendanceTypeDetails(row: Pick<
  AttendanceReportRow,
  'attendanceType' | 'officeName' | 'kioskName'
>): string {
  const lines: string[] = [];
  if (row.attendanceType) lines.push(row.attendanceType);
  if (row.officeName) lines.push(`Office: ${row.officeName}`);
  if (row.kioskName) lines.push(`Kiosk: ${row.kioskName}`);
  return lines.length > 0 ? lines.join('\n') : EMPTY;
}

/** Check-in/out cell: time, plus the source used for that punch when present. */
export function formatPunchTimeWithSource(
  time: string,
  sourceLabel: string | null | undefined,
): string {
  if (!time || time === EMPTY) return EMPTY;
  const label = sourceLabel?.trim();
  if (!label) return time;
  return `${time} — ${label}`;
}

/** @deprecated Use formatPunchTimeWithSource. */
export function formatPunchTimeWithKiosk(
  time: string,
  kioskName: string | null | undefined,
): string {
  return formatPunchTimeWithSource(time, kioskName);
}

export function mapAttendanceReportRows(
  rows: ReportPreviewRow[],
): AttendanceReportRow[] {
  return rows.map((row, index) => {
    const values = row.values;
    const employeeName = asDisplayText(firstValue(values, EMPLOYEE_KEYS));
    const employeeNumber = asDisplayText(
      firstValue(values, EMPLOYEE_NUMBER_KEYS),
    );
    const employee =
      employeeName !== EMPTY &&
      employeeNumber !== EMPTY &&
      employeeName !== employeeNumber
        ? `${employeeName} (${employeeNumber})`
        : employeeName !== EMPTY
        ? employeeName
        : employeeNumber;

    const checkIn = asTime(firstValue(values, CHECK_IN_KEYS));
    const checkOutRaw = firstValue(values, CHECK_OUT_KEYS);
    const checkOut = asTime(checkOutRaw);
    const hasCheckedOut = checkOut !== EMPTY;

    // No checkout → no work-hours UI value (dash). Early exit still has checkout.
    const workedKey = findSourceKey(values, WORKED_KEYS);
    const workHours = hasCheckedOut
      ? asWorkedHours(workedKey ? values[workedKey] : null, workedKey)
      : EMPTY;
    const breakKey = findSourceKey(values, BREAK_KEYS);
    const breakTime =
      checkIn === EMPTY
        ? EMPTY
        : asWorkedHours(breakKey ? values[breakKey] : 0, breakKey ?? 'breakMinutes');
    const expectedWorkMinutes = policyMinutes(
      asNumber(firstValue(values, EXPECTED_WORK_MINUTE_KEYS)),
    );
    const policyLunchBreakMinutes = asNumber(
      firstValue(values, POLICY_LUNCH_KEYS),
    );
    const workedMinutesForTotal = asNumber(
      workedKey ? values[workedKey] : null,
    );
    const breakMinutesForTotal = asNumber(
      breakKey ? values[breakKey] : checkIn === EMPTY ? null : 0,
    );
    const spreadMinutes = resolveAttendanceSpreadMinutes({
      spreadMinutes: asNumber(firstValue(values, SPREAD_KEYS)),
      expectedWorkMinutes,
      policyLunchBreakMinutes,
      actualBreakMinutes: breakMinutesForTotal,
    });
    const expectedHours =
      expectedWorkMinutes != null
        ? asWorkedHours(expectedWorkMinutes, 'expectedWorkMinutes')
        : EMPTY;
    const grossWorkMinutes = asNumber(
      firstValue(values, ['grossWorkMinutes', 'totalMinutes', 'durationMinutes']),
    );
    const totalWorkMinutes = hasCheckedOut
      ? actualTotalWorkMinutes({
          workedMinutes: workedMinutesForTotal,
          breakMinutes: breakMinutesForTotal,
          grossWorkMinutes,
        })
      : null;
    const totalHours =
      totalWorkMinutes != null
        ? formatTotalWorkHours(totalWorkMinutes, EMPTY)
        : EMPTY;
    const totalHoursCoverage = resolveTotalHoursCoverage(
      totalWorkMinutes,
      spreadMinutes,
    );
    const totalHoursCovered =
      totalHoursCoverage === 'unknown' ? null : totalHoursCoverage === 'covered';

    const status = resolveAttendanceReportStatus(values);
    const dayDetails = formatAttendanceReportDayDetails(values);
    const departmentValue = firstValue(values, DEPARTMENT_KEYS);
    const dateValue = firstValue(values, DATE_KEYS);
    const lateMinutes = asNumber(firstValue(values, LATE_MINUTE_KEYS));
    const isLate =
      asTruthyFlag(firstValue(values, LATE_FLAG_KEYS)) ||
      (typeof lateMinutes === 'number' && lateMinutes > 0) ||
      status.tone === 'late';
    const isMissingCheckout = detectMissingCheckout(values, hasCheckedOut);
    const workedKeyForCoverage = findSourceKey(values, WORKED_KEYS);
    // Covered / Not Covered / Short Hours only after a valid check-out.
    const hoursCoverage = resolveCheckedOutWorkHoursCoverage(
      hasCheckedOut,
      asNumber(workedKeyForCoverage ? values[workedKeyForCoverage] : null),
      asNumber(firstValue(values, EXPECTED_WORK_MINUTE_KEYS)),
    );
    const hoursCovered =
      hoursCoverage === 'unknown' ? null : hoursCoverage === 'covered';
    const incompleteKind = hasCheckedOut
      ? resolveIncompleteWorkKind(
          asNumber(workedKeyForCoverage ? values[workedKeyForCoverage] : null),
          asNumber(firstValue(values, EXPECTED_WORK_MINUTE_KEYS)),
        )
      : 'unknown';
    const attendanceModeRaw = firstValue(values, ATTENDANCE_MODE_KEYS);
    const attendanceMode =
      asDisplayText(attendanceModeRaw) === EMPTY
        ? null
        : asDisplayText(attendanceModeRaw);
    const checkInSource = optionalDisplay(
      firstValue(values, CHECK_IN_SOURCE_KEYS),
    );
    const checkOutSource = optionalDisplay(
      firstValue(values, CHECK_OUT_SOURCE_KEYS),
    );
    const checkInLocation = optionalDisplay(
      firstValue(values, CHECK_IN_LOCATION_KEYS),
    );
    const checkOutLocation = optionalDisplay(
      firstValue(values, CHECK_OUT_LOCATION_KEYS),
    );
    const checkInKioskName = optionalDisplay(
      firstValue(values, CHECK_IN_KIOSK_NAME_KEYS),
    );
    const checkOutKioskName = optionalDisplay(
      firstValue(values, CHECK_OUT_KIOSK_NAME_KEYS),
    );
    const checkInOriginalSource = optionalDisplay(
      firstValue(values, CHECK_IN_ORIGINAL_SOURCE_KEYS),
    );
    const checkOutOriginalSource = optionalDisplay(
      firstValue(values, CHECK_OUT_ORIGINAL_SOURCE_KEYS),
    );
    const checkInPunch = {
      source: checkInSource,
      originalSource: checkInOriginalSource,
      kioskName: checkInKioskName,
      kioskId: asNumber(firstValue(values, CHECK_IN_KIOSK_ID_KEYS)),
      faceVerified: asTruthyFlag(firstValue(values, CHECK_IN_FACE_KEYS))
        ? true
        : null,
      deviceIdentifier: optionalDisplay(
        firstValue(values, CHECK_IN_DEVICE_KEYS),
      ),
    };
    const checkOutPunch = {
      source: checkOutSource,
      originalSource: checkOutOriginalSource,
      kioskName: checkOutKioskName,
      kioskId: asNumber(firstValue(values, CHECK_OUT_KIOSK_ID_KEYS)),
      faceVerified: asTruthyFlag(firstValue(values, CHECK_OUT_FACE_KEYS))
        ? true
        : null,
      deviceIdentifier: optionalDisplay(
        firstValue(values, CHECK_OUT_DEVICE_KEYS),
      ),
    };
    const checkInSourceLabel =
      checkIn === EMPTY && !checkInSource && !checkInKioskName
        ? null
        : punchActionSourceLabel(checkInPunch);
    const checkOutSourceLabel =
      checkOut === EMPTY && !checkOutSource && !checkOutKioskName
        ? null
        : punchActionSourceLabel(checkOutPunch);
    const highlightCheckOutSource =
      hasCheckedOut && isMixedPunchActionSource(checkInPunch, checkOutPunch);
    const locationMode = resolveAttendanceLocationMode({
      attendanceMode,
      checkIn:
        checkIn === EMPTY && !checkInSource && !checkInLocation
          ? null
          : {
              source: checkInSource,
              locationName: checkInLocation,
              localTimestamp: checkIn === EMPTY ? null : checkIn,
            },
      checkOut:
        checkOut === EMPTY && !checkOutSource && !checkOutLocation
          ? null
          : {
              source: checkOutSource,
              locationName: checkOutLocation,
              localTimestamp: checkOut === EMPTY ? null : checkOut,
            },
    });
    const checkInChannel =
      checkIn === EMPTY && !checkInSource
        ? null
        : attendancePunchChannelLabel(checkInSource, checkInPunch);
    const checkOutChannel =
      checkOut === EMPTY && !checkOutSource
        ? null
        : attendancePunchChannelLabel(checkOutSource, checkOutPunch);
    const attendanceType = formatCheckInCheckOutType(
      checkInChannel,
      checkOutChannel,
    );
    const officeName = formatSharedOrPairedLabel(
      checkInLocation,
      checkOutLocation,
    );
    const kioskName = formatSharedOrPairedLabel(
      checkInKioskName,
      checkOutKioskName,
    );

    const backendStatus = dayStatusFromReportValues(values);
    let statusLabel = status.label;
    let statusTone = status.tone;
    // Rows without a backend day status: band by worked vs expected hours.
    if (!backendStatus) {
      if (
        hasCheckedOut &&
        incompleteKind === 'covered' &&
        statusTone === 'shortHours'
      ) {
        statusLabel = isLate ? 'Late' : 'Present';
        statusTone = isLate ? 'late' : 'present';
      } else if (
        hasCheckedOut &&
        incompleteKind === 'half_day_absent' &&
        statusTone !== 'leave' &&
        statusTone !== 'wfh'
      ) {
        statusLabel = 'Half-Day Absent';
        statusTone = 'absent';
      } else if (
        hasCheckedOut &&
        incompleteKind === 'short_hours' &&
        statusTone !== 'leave' &&
        statusTone !== 'wfh'
      ) {
        statusLabel = 'Short Hours';
        statusTone = 'shortHours';
      } else if (
        hasCheckedOut &&
        hoursCovered === false &&
        incompleteKind === 'unknown' &&
        statusTone !== 'leave' &&
        statusTone !== 'absent' &&
        statusTone !== 'wfh'
      ) {
        statusLabel = 'Short Hours';
        statusTone = 'shortHours';
      }
    }

    // With a backend day status, short hours is worked < expected after
    // check-out on a day that is not leave / WFH.
    const isShortHours = backendStatus
      ? hoursCovered === false && statusTone !== 'leave' && statusTone !== 'wfh'
      : hoursCovered === true
        ? false
        : incompleteKind === 'half_day_absent'
          ? false
          : detectShortHours(values) || statusTone === 'shortHours';

    return {
      rowKey: row.rowKey || `attendance-report-${index}`,
      employee,
      status: statusLabel,
      statusNotes: backendStatus?.notes ?? [],
      trackingAttendanceId: asNumber(values.trackingAttendanceId) ?? null,
      trackingStatus: optionalDisplay(values.trackingStatus),
      statusTone,
      checkIn,
      checkOut,
      workHours,
      breakTime,
      expectedHours,
      totalHours,
      totalHoursCovered,
      dayDetails,
      department:
        departmentValue === null ? null : asDisplayText(departmentValue),
      attendanceDate: dateValue === null ? null : asDisplayText(dateValue),
      hasCheckedOut,
      isLate,
      isShortHours,
      isMissingCheckout,
      hoursCovered,
      locationMode,
      attendanceType,
      officeName,
      kioskName,
      checkInKioskName,
      checkOutKioskName,
      checkInSourceLabel,
      checkOutSourceLabel,
      highlightCheckOutSource,
    };
  });
}

function normalizedStatusFilter(status: string): string {
  return status.trim().toLowerCase().replace(/[_-]+/g, ' ');
}

export function rowMatchesAttendanceStatus(
  row: AttendanceReportRow,
  status: string,
): boolean {
  const normalized = normalizedStatusFilter(status);
  if (!normalized) return true;
  if (normalized === 'short hours' || normalized === 'shorthours') {
    return row.isShortHours;
  }
  if (normalized === 'missing checkout' || normalized === 'missing check out') {
    return row.isMissingCheckout;
  }
  if (normalized === 'late') {
    return row.isLate || row.statusTone === 'late';
  }
  if (normalized === 'absent' || normalized === 'lop') {
    return row.statusTone === 'absent';
  }
  if (normalized === 'present') {
    return (
      row.statusTone === 'present' ||
      row.statusTone === 'late' ||
      row.statusTone === 'shortHours'
    );
  }
  if (normalized === 'on leave' || normalized === 'leave') {
    return row.statusTone === 'leave';
  }
  if (normalized === 'wfh') {
    return row.statusTone === 'wfh';
  }
  if (row.status === EMPTY) return false;
  return row.status.toLowerCase().includes(normalized);
}

export function filterAttendanceReportRows(
  rows: AttendanceReportRow[],
  filters: Pick<AttendanceReportFilters, 'department' | 'status'>,
): AttendanceReportRow[] {
  const department = filters.department.trim().toLowerCase();
  const departmentIsId = /^\d+$/.test(department);

  return rows.filter(row => {
    if (department && !departmentIsId && row.department !== null) {
      if (!row.department.toLowerCase().includes(department)) return false;
    }
    if (!rowMatchesAttendanceStatus(row, filters.status)) return false;
    return true;
  });
}

export function summarizeAttendanceReportRows(rows: AttendanceReportRow[]): {
  present: number;
  late: number;
  absent: number;
  onLeave: number;
  wfh: number;
  shortHours: number;
  missingCheckout: number;
} {
  const summary = {
    present: 0,
    late: 0,
    absent: 0,
    onLeave: 0,
    wfh: 0,
    shortHours: 0,
    missingCheckout: 0,
  };
  for (const row of rows) {
    if (row.isShortHours) summary.shortHours += 1;
    if (row.isMissingCheckout) summary.missingCheckout += 1;
    if (row.isLate || row.statusTone === 'late') summary.late += 1;
    switch (row.statusTone) {
      case 'present':
        summary.present += 1;
        break;
      case 'shortHours':
        summary.present += 1;
        break;
      case 'late':
        summary.present += 1;
        break;
      case 'absent':
        summary.absent += 1;
        break;
      case 'leave':
        summary.onLeave += 1;
        break;
      case 'wfh':
        summary.wfh += 1;
        break;
      default:
        break;
    }
  }
  return summary;
}
